import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Leg } from '../core/_models'
import { updateLeg } from '../core/_requests'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'
import PortSelect from '../../../shared/components/PortSelect'

interface Props {
  isOpen: boolean
  onClose: () => void
  onLegUpdated: (leg: Leg) => void
  voyageId: number
  leg: Leg
}

/**
 * Modal for editing an existing Leg within a Voyage.
 * Pre-populates all fields from the existing leg data.
 * Sequence number is read-only (cannot be changed after creation).
 */
const EditLegModal: React.FC<Props> = ({ isOpen, onClose, onLegUpdated, voyageId, leg }) => {
  const [departurePort, setDeparturePort] = useState('')
  const [arrivalPort, setArrivalPort] = useState('')
  const [legStartDate, setLegStartDate] = useState('')
  const [legEndDate, setLegEndDate] = useState('')
  const [eta, setEta] = useState('')
  const [etd, setEtd] = useState('')
  const [cosp, setCosp] = useState('')
  const [eosp, setEosp] = useState('')
  const [estimatedDistance, setEstimatedDistance] = useState('')
  const [loadingCondition, setLoadingCondition] = useState('')
  const [draftFore, setDraftFore] = useState('')
  const [draftMid, setDraftMid] = useState('')
  const [draftAft, setDraftAft] = useState('')
  const [totalCargoOnboard, setTotalCargoOnboard] = useState('')
  const [cpSpeed, setCpSpeed] = useState('')
  const [cpConsumptionTotal, setCpConsumptionTotal] = useState('')
  const [agentName, setAgentName] = useState('')
  const [agentContactNumber, setAgentContactNumber] = useState('')
  const [agentTelephoneNumber, setAgentTelephoneNumber] = useState('')
  const [agentFaxAddress, setAgentFaxAddress] = useState('')
  const [agentEmail, setAgentEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Populate form from leg data whenever the modal opens
  useEffect(() => {
    if (isOpen && leg) {
      setDeparturePort(leg.departurePort ?? '')
      setArrivalPort(leg.arrivalPort ?? '')
      setLegStartDate(leg.legStartDate ?? '')
      setLegEndDate(leg.legEndDate ?? '')
      setEta(leg.eta ?? '')
      setEtd(leg.etd ?? '')
      setCosp(leg.cosp ?? '')
      setEosp(leg.eosp ?? '')
      setEstimatedDistance(leg.estimatedDistance != null ? String(leg.estimatedDistance) : '')
      setLoadingCondition(leg.loadingCondition ?? '')
      setDraftFore(leg.draftFore != null ? String(leg.draftFore) : '')
      setDraftMid(leg.draftMid != null ? String(leg.draftMid) : '')
      setDraftAft(leg.draftAft != null ? String(leg.draftAft) : '')
      setTotalCargoOnboard(leg.totalCargoOnboard != null ? String(leg.totalCargoOnboard) : '')
      setCpSpeed(leg.cpSpeed != null ? String(leg.cpSpeed) : '')
      setCpConsumptionTotal(leg.cpConsumptionTotal != null ? String(leg.cpConsumptionTotal) : '')
      setAgentName(leg.agentName ?? '')
      setAgentContactNumber(leg.agentContactNumber ?? '')
      setAgentTelephoneNumber(leg.agentTelephoneNumber ?? '')
      setAgentFaxAddress(leg.agentFaxAddress ?? '')
      setAgentEmail(leg.agentEmail ?? '')
      setError('')
    }
  }, [isOpen, leg])

  const handleClose = () => {
    setError('')
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!departurePort.trim()) {
      setError('Departure Port is required.')
      toast.error('Departure Port is required.')
      return
    }

    setIsSubmitting(true)
    try {
      const payload: Partial<Leg> = {
        departurePort: departurePort.trim(),
        arrivalPort: arrivalPort.trim() || undefined,
        legStartDate: legStartDate || undefined,
        legEndDate: legEndDate || undefined,
        eta: eta || undefined,
        etd: etd || undefined,
        cosp: cosp || undefined,
        eosp: eosp || undefined,
        estimatedDistance: estimatedDistance ? parseFloat(estimatedDistance) : undefined,
        loadingCondition: loadingCondition || undefined,
        draftFore: draftFore ? parseFloat(draftFore) : undefined,
        draftMid: draftMid ? parseFloat(draftMid) : undefined,
        draftAft: draftAft ? parseFloat(draftAft) : undefined,
        totalCargoOnboard: totalCargoOnboard ? parseFloat(totalCargoOnboard) : undefined,
        cpSpeed: cpSpeed ? parseFloat(cpSpeed) : undefined,
        cpConsumptionTotal: cpConsumptionTotal ? parseFloat(cpConsumptionTotal) : undefined,
        agentName: agentName || undefined,
        agentContactNumber: agentContactNumber || undefined,
        agentTelephoneNumber: agentTelephoneNumber || undefined,
        agentFaxAddress: agentFaxAddress || undefined,
        agentEmail: agentEmail || undefined,
      }
      const updated = await updateLeg(voyageId, leg.id, payload)
      toast.success('Leg updated successfully')
      onLegUpdated(updated)
      handleClose()
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Error updating leg')
      setError(msg)
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable my-2 my-sm-4">
        <div
          className="modal-content"
          style={{
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div className="modal-header flex-shrink-0">
            <h5 className="modal-title">
              Edit Leg {leg.sequenceNumber} &nbsp;
              <span className="badge bg-secondary">{leg.departurePort} → {leg.arrivalPort ?? '?'}</span>
            </h5>
            <button type="button" className="btn-close" onClick={handleClose} />
          </div>

          <form
            onSubmit={handleSubmit}
            className="d-flex flex-column flex-grow-1 overflow-hidden"
            style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <div
              className="modal-body overflow-y-auto"
              style={{
                flex: 1,
                minHeight: 0,
                maxHeight: 'calc(90vh - 130px)',
              }}
            >
              {error && <div className="alert alert-danger">{error}</div>}

              {/* Read-only sequence info */}
              <div className="alert alert-secondary py-2 mb-4">
                <strong>Leg {leg.sequenceNumber}</strong> — sequence number cannot be changed.
              </div>

              {/* Port Details */}
              <h6 className="fw-bold text-uppercase mb-3 border-bottom pb-2">Port Details</h6>
              <div className="row mb-4">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label fw-bold">Departure Port <span className="text-danger">*</span></label>
                  <PortSelect
                    value={departurePort}
                    onChange={(e) => setDeparturePort(e.target.value)}
                    placeholder="Select or search departure port..."
                    required
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label fw-bold">Arrival Port <small className="text-muted">(Optional)</small></label>
                  <PortSelect
                    value={arrivalPort}
                    onChange={(e) => setArrivalPort(e.target.value)}
                    placeholder="Select or search arrival port..."
                  />
                </div>
              </div>

              {/* Dates */}
              <h6 className="fw-bold text-uppercase mb-3 border-bottom pb-2">Leg Dates</h6>
              <div className="row mb-4">
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">Departure Date/Time</label>
                  <input type="datetime-local" className="form-control" value={legStartDate} onChange={(e) => setLegStartDate(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">Arrival Date/Time</label>
                  <input type="datetime-local" className="form-control" value={legEndDate} onChange={(e) => setLegEndDate(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">ETD</label>
                  <input type="datetime-local" className="form-control" value={etd} onChange={(e) => setEtd(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">ETA</label>
                  <input type="datetime-local" className="form-control" value={eta} onChange={(e) => setEta(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">COSP</label>
                  <input type="datetime-local" className="form-control" value={cosp} onChange={(e) => setCosp(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">EOSP</label>
                  <input type="datetime-local" className="form-control" value={eosp} onChange={(e) => setEosp(e.target.value)} />
                </div>
              </div>

              {/* Operational Metrics */}
              <h6 className="fw-bold text-uppercase mb-3 border-bottom pb-2">Operational Metrics</h6>
              <div className="row mb-4">
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">Estimated Distance (NM)</label>
                  <input type="number" className="form-control" value={estimatedDistance} onChange={(e) => setEstimatedDistance(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">Loading Condition</label>
                  <select className="form-select" value={loadingCondition} onChange={(e) => setLoadingCondition(e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Laden">Laden</option>
                    <option value="Ballast">Ballast</option>
                    <option value="Part Laden">Part Laden</option>
                  </select>
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">Draft Fore (m)</label>
                  <input type="number" step="0.01" className="form-control" value={draftFore} onChange={(e) => setDraftFore(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">Draft Mid (m)</label>
                  <input type="number" step="0.01" className="form-control" value={draftMid} onChange={(e) => setDraftMid(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">Draft Aft (m)</label>
                  <input type="number" step="0.01" className="form-control" value={draftAft} onChange={(e) => setDraftAft(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">Total Cargo Onboard (MT)</label>
                  <input type="number" step="0.001" className="form-control" value={totalCargoOnboard} onChange={(e) => setTotalCargoOnboard(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">CP Speed (kts)</label>
                  <input type="number" step="0.1" className="form-control" value={cpSpeed} onChange={(e) => setCpSpeed(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <label className="form-label fw-bold">CP Consumption Total</label>
                  <input type="number" step="0.1" className="form-control" value={cpConsumptionTotal} onChange={(e) => setCpConsumptionTotal(e.target.value)} />
                </div>
              </div>

              {/* Agent Details */}
              <h6 className="fw-bold text-uppercase mb-3 border-bottom pb-2">Agent Details</h6>
              <div className="row mb-3">
                <div className="col-12 col-sm-6 col-md-4 mb-3">
                  <label className="form-label fw-bold">Agent Name</label>
                  <input type="text" className="form-control" value={agentName} onChange={(e) => setAgentName(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 mb-3">
                  <label className="form-label fw-bold">Contact Number</label>
                  <input type="text" className="form-control" value={agentContactNumber} onChange={(e) => setAgentContactNumber(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 mb-3">
                  <label className="form-label fw-bold">Telephone</label>
                  <input type="text" className="form-control" value={agentTelephoneNumber} onChange={(e) => setAgentTelephoneNumber(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 mb-3">
                  <label className="form-label fw-bold">Fax</label>
                  <input type="text" className="form-control" value={agentFaxAddress} onChange={(e) => setAgentFaxAddress(e.target.value)} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 mb-3">
                  <label className="form-label fw-bold">Email</label>
                  <input type="email" className="form-control" value={agentEmail} onChange={(e) => setAgentEmail(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="modal-footer flex-shrink-0 bg-white">
              <button type="button" className="btn btn-light" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><span className="spinner-border spinner-border-sm me-2" />Saving...</>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditLegModal
